import { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, useLoaderData, useParams } from 'react-router-dom';
import { axiosClient } from '../../../library/Service/axiosClient';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import Swal from 'sweetalert2';
import SessionContext from '../../../library/Core/SessionContext';

const UploadTrackerLayout = (props: any) => {
    const params = useParams();
    const { sessionState } = useContext(SessionContext);
    const [file, setFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadType, setUploadType] = useState('RAW_DATA');
    const [comment, setComment] = useState('');
    const [isLoading, setIsLoading] = useState(false); // Loading state
    const [isFormSubmitted, setIsFormSubmitted] = useState(false); // Loading state
    const [uploadProgress, setUploadProgress] = useState(0); // Progress bar state
    const [successFlag, setSuccessFlag] = useState(false);
    const [invalidData, setInvalidData] = useState([]);
    const [validData, setValidData] = useState([]);
    
    // Handle change in type
    const handleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setUploadType(event.target.value);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
        }
    };

    const clearFile = () => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!file) {
            console.error('No file selected');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('uploadType', uploadType);
        formData.append('comment', comment);
        formData.append('instituteId', `${params.institute_id}`);
        formData.append('uploadedById', `${sessionState?.id}`);

        const url = `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/raw-user/upload-tracker-data`;

        try {
            setIsLoading(true); // Start loading
            setUploadProgress(0); // Reset progress

            let uploadResponse = await axiosClient().post(url, formData, {
                onUploadProgress: (progressEvent) => {
                    if (progressEvent && progressEvent.loaded !== undefined && progressEvent.total !== undefined) {
                        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        setUploadProgress(progress); // Update progress
                    } else {
                        console.warn('Progress information is missing or incomplete.');
                        setUploadProgress(0); // Default to 0 if progress cannot be calculated
                    }
                },
            });
            
            const { success, message } = uploadResponse.data;
            

            setIsFormSubmitted(true)
            if (uploadResponse.data.invalidRecords && uploadResponse.data.invalidRecords.length > 0) {
                setInvalidData(uploadResponse.data.invalidRecords);
                clearFile();
                setValidData([]);
            } else {
                setInvalidData([]);
                clearFile();
                setValidData(uploadResponse.data.data || []);
            }
            setSuccessFlag(success);

            if (success) {
                Swal.fire('Success', message || 'File uploaded successfully', 'success');
            } else {
                Swal.fire('Error', message || 'File upload failed', 'error');
            }

            console.log('File uploaded successfully');
        } catch (error) {
            console.error('Error uploading file:', error);
            clearFile();
            Swal.fire('Error', 'An error occurred while uploading the file. Please try again.', 'error');
        } finally {
            setIsLoading(false); // End loading
        }
    };

    return (
        <div className="container max-1140 px-lg-0 overflow-hidden">
            <div className="row">
                <div className="col-md-12 mb-4">
                    <div className="container mt-5">
                        <h2 className="mb-4">Upload Field Updates</h2>

                        <div className="alert alert-info">
                            <h5>Please Note:</h5>
                            <ul>
                                <li>The file must be in Excel format (.xlsx).</li>
                                <li>
                                <strong>Field Names</strong> column must match exactly as per the sample file.
                                </li>
                                {/* 
                                <li>
                                    <strong>Email and Phone Number</strong> must be valid.
                                </li> */}
                            </ul>
                            <a href="https://company-logo-list.s3.ap-south-1.amazonaws.com/Tracker+Data+Sample+File.xlsx" className="btn btn-outline-primary me-2" download>
                                Download Sample Excel File
                            </a>
                        </div>

                        <form onSubmit={handleSubmit} encType="multipart/form-data">
                            <div className="mb-3">
                                <label htmlFor="user_type" className="form-label">
                                    Upload Type *
                                </label>
                                <select disabled className="form-control" name="user_type" id="user_type" defaultValue='RAW_DATA' onChange={handleTypeChange}>
                                    {/* <option value="FIELD_UPDATES">Field Updates</option> */}
                                    <option value="RAW_DATA">Data Received from the Institute (Initial)</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label htmlFor="user_type" className="form-label">
                                    Comment
                                </label>
                                <input className="form-control" type="text" name="comment" id="comment" onChange={(e) => setComment(e.target.value)}  />
                            </div>
                            <div className="mb-3">
                                <label htmlFor="fileInput" className="form-label">
                                    Choose file (Only Excel)
                                </label>
                                <input
                                    ref={fileInputRef}
                                    className="form-control"
                                    type="file"
                                    id="fileInput"
                                    accept=".xlsx"
                                    onChange={handleFileChange}
                                    required
                                />
                                {/* show preview of uploaded file */}
                            </div>
                            {/* {!isFormSubmitted && ( */}
                                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                    {isLoading ? 'Uploading...' : 'Upload'}
                                </button>
                            {/* )} */}
                        </form>
                        {isLoading && (
                            <div className="progress mt-3">
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{ width: `${uploadProgress}%` }}
                                    aria-valuenow={uploadProgress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}>
                                    {uploadProgress}%
                                </div>
                            </div>
                        )}
                        {invalidData.length > 0 && (
                            <div className="mt-5 p-4 border border-danger rounded-3" style={{ backgroundColor: '#fff5f5' }}>
                                <div className="d-flex align-items-center mb-4">
                                    <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '1.5rem', color: '#dc3545', marginRight: '12px' }}></i>
                                    <div>
                                        <h4 className="text-danger mb-1"> Invalid Records Found</h4>
                                        <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                            {invalidData.length} record(s) failed validation. Please review and correct the highlighted issues below.
                                        </p>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-hover0 table-striped0 mb-0" style={{ 
                                        backgroundColor: '#fff',
                                        border: '1px solid #dc3545'
                                    }}>
                                        <thead className="bg-danger text-white">
                                            <tr>
                                                <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                                                {Object.keys(invalidData[0]).map((key) => (
                                                    <th key={key} style={{ minWidth: '150px' }}>{key}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invalidData.map((record: any, index: number) => (
                                                <tr key={index} style={{ borderLeftColor: '#dc3545', borderLeftWidth: '1px' }}>
                                                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#dc3545' }}>{index + 1}</td>
                                                    {Object.values(record).map((value: any, idx: number) => (
                                                        <td key={idx} style={{ color: '#666' }}>{value}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {isFormSubmitted && invalidData.length === 0 && successFlag && !isLoading && (
                            <div className="mt-5 p-4 border border-success rounded-3" style={{ backgroundColor: '#f0fdf4' }}>
                                <div className="d-flex align-items-center mb-4">
                                    <i className="bi bi-check-circle-fill" style={{ fontSize: '1.5rem', color: '#198754', marginRight: '12px' }}></i>
                                    <div>
                                        <h4 className="text-success mb-1">✓ All Records Inserted</h4>
                                        <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                            {validData.length} record(s) processed successfully with no errors.
                                        </p>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-hover0 table-striped0 mb-0" style={{ 
                                        backgroundColor: '#fff',
                                        border: '1px solid #198754'
                                    }}>
                                        <thead className="bg-success text-white">
                                            <tr>
                                                <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                                                {validData.length > 0 && Object.keys(validData[0]).map((key) => (
                                                    <th key={key} style={{ minWidth: '150px' }}>{key}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {validData.map((record: any, index: number) => (
                                                <tr key={index} style={{ borderLeftColor: '#198754', borderLeftWidth: '1px' }}>
                                                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#198754' }}>{index + 1}</td>
                                                    {Object.values(record).map((value: any, idx: number) => (
                                                        <td key={idx} style={{ color: '#666' }}>{value}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadTrackerLayout;
